import { collectEvent, UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAnswerPageConfig } from '/@contexts/answer-context';
import { fireSessionLatch } from '/@services/pinboard/pinboard-cache/cache-hydration-event';
import { BlinkErrorCode } from '/@utils/error-handling/blink-error-boundary';
import {
    DocumentNode,
    MutationHookOptions,
    QueryHookOptions,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    useBachLazyQuery,
    useBachMutation,
} from './bach';
import {
    BachMutationFunctionOptions,
    BachMutationHookOptions,
    BachMutationTuple,
} from './bach.util';

export function useHydrationGate() {
    let isHydrated = true;
    let displayedVizId: string | undefined;
    try {
        ({
            isHydrated = true,
            displayedVizId,
        } = getAnswerPageConfig());
    } catch {
        // Outside answer-context; treat as already hydrated.
    }
    const isReady = isHydrated !== false;
    const isReadyRef = useRef(isReady);
    isReadyRef.current = isReady;

    // Held in a ref so gateCall keeps a stable identity.
    const vizIdRef = useRef(displayedVizId);
    vizIdRef.current = displayedVizId;

    const pendingCallRef = useRef<(() => void) | null>(null);
    const [isPending, setIsPending] = useState(false);

    useEffect(() => {
        if (isReady && pendingCallRef.current) {
            const call = pendingCallRef.current;
            pendingCallRef.current = null;
            setIsPending(false);
            call();
        }
    }, [isReady]);

    const gateCall = useCallback(
        <T>(execute: () => Promise<T>): Promise<T> => {
            if (isReadyRef.current) {
                return execute();
            }
            // Signal the cache latch when about to defer — breaks table
            // tile deadlock.
            const vizId = vizIdRef.current;
            if (vizId) {
                fireSessionLatch(vizId);
            }
            return new Promise<T>((resolve, reject) => {
                pendingCallRef.current = () => {
                    // eslint-disable-next-line promise/catch-or-return
                    execute().then(resolve, reject);
                };
                setIsPending(true);
            });
        },
        [],
    );

    return { isReady, isWaiting: isPending, gateCall };
}

/**
 * Hydration-aware wrapper over useBachMutation.
 * Defers the mutation call until isHydrated is true, reporting
 * loading while waiting.
 */
export function useGatedBachMutation<TData, TVariables>(
    sessionId: string,
    mutation: DocumentNode,
    options: BachMutationHookOptions<TData, TVariables> = {},
    handledErrors?: BlinkErrorCode[],
    isPinboardSession = false,
    sendAcSessionInfo = false,
): BachMutationTuple<
    TData,
    Omit<TVariables, 'session' | 'dataPaginationParams'>
> {
    const { isReady, isWaiting, gateCall } = useHydrationGate();
    const [mutate, state] = useBachMutation(
        sessionId,
        mutation,
        options,
        handledErrors,
        isPinboardSession,
        sendAcSessionInfo,
    );

    const mutationName = (mutation.definitions[0] as any)?.name?.value;

    const mutateRef = useRef(mutate);
    mutateRef.current = mutate;
    const gatedMutate = useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            if (!isReady) {
                collectEvent(
                    new UserAction(
                        userActionTypeEnum.GATED_BACH_DEFERRED,
                    ),
                    { mutationName, type: 'mutation' },
                );
            }
            return gateCall(() => mutateRef.current(opts));
        },
        [gateCall, isReady, mutationName],
    );

    const result = useMemo(
        () => (isWaiting ? { ...state, loading: true } : state),
        [isWaiting, state],
    );

    return [gatedMutate, result];
}

/**
 * Hydration-aware wrapper over useBachLazyQuery.
 * Defers the execute call until isHydrated is true, reporting
 * loading while waiting.
 */
export function useGatedBachLazyQuery<TData, TVariables>(
    sessionId: string,
    query: DocumentNode,
    options:
        | MutationHookOptions<TData, TVariables>
        | QueryHookOptions<TData, TVariables> = {},
    sendAcSessionInfo = false,
): ReturnType<typeof useBachLazyQuery<TData, TVariables>> {
    const { isReady, isWaiting, gateCall } = useHydrationGate();
    const [execute, state] = useBachLazyQuery(
        sessionId,
        query,
        options,
        sendAcSessionInfo,
    );

    const queryName = (query.definitions[0] as any)?.name?.value;

    const executeRef = useRef(execute);
    executeRef.current = execute;

    const gatedExecute = useCallback(
        (opts?: Parameters<typeof execute>[0]) => {
            if (!isReady) {
                collectEvent(
                    new UserAction(
                        userActionTypeEnum.GATED_BACH_DEFERRED,
                    ),
                    { queryName, type: 'query' },
                );
            }
            return gateCall(() => executeRef.current(opts));
        },
        [gateCall, isReady, queryName],
    );

    // refetch does not need to be gated:
    // 1. Apollo's refetchWithSession returns Promise.resolve() if called before execute
    //    (query hasn't run yet), so early calls are already safe no-ops.
    // 2. execute is gated, so refetch only becomes meaningful after execute succeeds
    //    (post-hydration). By that point isHydrated is already true.
    const result = useMemo(
        () => (isWaiting ? { ...state, loading: true } : state),
        [isWaiting, state],
    );

    return [gatedExecute, result];
}